import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import whatsappService from '../services/whatsapp.service';
import type { Wazzup24Instance } from '../types/whatsapp.types';
import { usePermissions } from '../utils/usePermissions';
import { useAppFeatures } from './AppFeaturesContext';
import { safeGetItem, safeSetItem, safeRemoveItem, safeGetJson, safeSetJson } from '../utils/safeStorage';

interface Wazzup24ContextType {
  instances: Wazzup24Instance[];
  currentInstance: Wazzup24Instance | null;
  isLoading: boolean;
  error: string | null;
  setCurrentInstance: (instance: Wazzup24Instance | null) => void;
  refreshInstances: () => Promise<void>;
}

const Wazzup24Context = createContext<Wazzup24ContextType | null>(null);

const STORAGE_KEY = 'wazzup24_selected_instance_id';
const STORAGE_KEY_DATA = 'wazzup24_selected_instance_data';

const readStoredInstance = (): Wazzup24Instance | null => {
  if (typeof window === 'undefined') return null;
  const parsed = safeGetJson<Wazzup24Instance | null>(STORAGE_KEY_DATA, null);
  if (
    parsed !== null &&
    typeof parsed === 'object' &&
    typeof parsed.id === 'number' &&
    typeof parsed.name === 'string' &&
    typeof parsed.channel_id === 'string' &&
    typeof parsed.is_active === 'boolean'
  ) {
    return parsed;
  }
  return null;
};

export const Wazzup24Provider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [instances, setInstances] = useState<Wazzup24Instance[]>([]);
  const [currentInstance, setCurrentInstanceState] = useState<Wazzup24Instance | null>(() => readStoredInstance());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const appFeatures = useAppFeatures();
  const canListInstances = hasPermission('integrations:wazzup24:instances:list');

  const loadInstances = useCallback(async () => {
    // Проверка: фича Wazzup24 должна быть включена
    if (!appFeatures.wazzup) {
      setInstances([]);
      setCurrentInstanceState(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (!canListInstances) {
      setInstances((prev) => (prev.length ? [] : prev));
      setCurrentInstanceState((prev) => (prev ? null : prev));
      setError((prev) => (prev === 'Недостаточно прав для просмотра инстансов Wazzup24' ? prev : 'Недостаточно прав для просмотра инстансов Wazzup24'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await whatsappService.getWazzup24Instances();
      setInstances(data);

      // Выбираем инстанс по приоритету:
      // 1. Сохраненный в localStorage (если он есть в списке и активен)
      // 2. Первый активный инстанс
      // 3. Первый инстанс (даже если неактивен)
      // 4. null (если список пуст)

      let selected: Wazzup24Instance | null = null;

      if (data.length > 0) {
        // Пытаемся восстановить сохраненный инстанс
        const savedId = safeGetItem(STORAGE_KEY);
        if (savedId) {
          const savedInstance = data.find(inst => inst.id === Number(savedId) && inst.is_active);
          if (savedInstance) {
            selected = savedInstance;
          }
        }

        // Если не нашли сохраненный, выбираем первый активный
        if (!selected) {
          selected = data.find(inst => inst.is_active) || data[0];
        }
      }

      setCurrentInstanceState(selected);

      // Сохраняем выбор
      if (selected) {
        safeSetItem(STORAGE_KEY, String(selected.id));
        safeSetJson(STORAGE_KEY_DATA, selected);
      } else {
        safeRemoveItem(STORAGE_KEY);
        safeRemoveItem(STORAGE_KEY_DATA);
      }
    } catch (err: any) {
      console.error('[Wazzup24Context] Failed to load instances:', err);
      setError(err?.message || 'Не удалось загрузить список инстансов');
      setInstances([]);
      setCurrentInstanceState(null);
    } finally {
      setIsLoading(false);
    }
  }, [appFeatures.wazzup, canListInstances]);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  const setCurrentInstance = useCallback((instance: Wazzup24Instance | null) => {
    setCurrentInstanceState(instance);
    if (instance) {
      safeSetItem(STORAGE_KEY, String(instance.id));
      safeSetJson(STORAGE_KEY_DATA, instance);
    } else {
      safeRemoveItem(STORAGE_KEY);
      safeRemoveItem(STORAGE_KEY_DATA);
    }
  }, []);

  const refreshInstances = useCallback(async () => {
    await loadInstances();
  }, [loadInstances]);

  const value = useMemo<Wazzup24ContextType>(() => ({
    instances,
    currentInstance,
    isLoading,
    error,
    setCurrentInstance,
    refreshInstances,
  }), [instances, currentInstance, isLoading, error, setCurrentInstance, refreshInstances]);

  return (
    <Wazzup24Context.Provider value={value}>
      {children}
    </Wazzup24Context.Provider>
  );
};

export const useWazzup24 = (): Wazzup24ContextType => {
  const context = useContext(Wazzup24Context);
  if (!context) {
    throw new Error('useWazzup24 must be used within Wazzup24Provider');
  }
  return context;
};
